import type {
    CreateExtArgs,
    CreateExtensionArgument,
    DmApis,
    Extension,
    ExtensionAPI,
    ExtensionPublicAPI,
    InitializeExtArgs
} from '@wix/document-manager-core'
import type {STExtApi} from '../serviceTopology'
import type {SchemaAPI, SchemaExtensionAPI} from '../schema/schema'
import type {RMApi} from '../rendererModel'
import {isEmpty} from 'lodash'

import {type BuilderCompDetails, deriveFromManifest, type Schema} from './manifestSchemaUtils'
import type {FetchFn} from '../../utils/fetchUtils'
import type {Builder, BuilderTypes, CompRef, Experiment, SchemaFile} from '@wix/document-services-types'

export type {BuilderCompDetails} from './manifestSchemaUtils'

import type {PlatformExtensionAPI} from '../platform'

import {builderTypes} from '../../utils/builderUtils'
import {getComponentType} from '../../utils/dalUtils'
import type {HooksExtensionApi} from '../hooks/hooks'
import {
    type AfterPublicGetDataItemEvent,
    type BeforePublicUpdateDataItemEvent,
    DATA_MODEL_HOOKS
} from '../dataModel/hooks'
import {
    transformBuilderDataToFlat,
    transformBuilderDataToNestedObjectsOnDataUpdate,
    transformBuilderDataToNestedObjectsOnComponentAdd,
    transformInnerElementsBuilderDataToFlat
} from './builderComponentsHooks'
import {type ComponentBeforeAddDataEvent, COMPONENTS_HOOKS, type SerializeDataAfterEvent} from '../components/hooks'
import type {ContentFill} from '@wix/ambassador-devcenter-v1-component-type-data/types'

interface BuilderComponentsApi extends ExtensionAPI {
    setManifests(manifests: Record<string, Builder.ResolvedManifest>): void
    registerBuilderComponentsForApp(appData: any): Promise<void>
    deriveFromManifest(identifier: string, manifest: Builder.ResolvedManifest): BuilderCompDetails[]
    registerBuilderComponent(details: BuilderCompDetails): void
    registerBuilderComponentWithManifest(identifier: string, manifest: Builder.ResolvedManifest): void
    getBuilderTypes(compPointer: CompRef): BuilderTypes
    isBuilder(compType: string): boolean
    layout: {
        getContentFill(compPointer: CompRef): ContentFill | undefined
    }
}

interface BuilderComponentsExtensionAPI extends ExtensionAPI {
    builderComponents: BuilderComponentsApi
}

interface BuilderComponentsPublicExtensionAPI extends ExtensionPublicAPI {
    builderComponents: {
        setManifests: BuilderComponentsApi['setManifests']
    }
}

const getAppComponents = (appData: any) => appData.components ?? []

const builderAppTypes = new Set(['STUDIO_WIDGET', 'EDITOR_REACT_COMPONENT'])
const isNewBuilderComponent = (compData: any) =>
    builderAppTypes.has(compData.type) && !isEmpty(compData.data?.componentModel)

const extractBuilderCompsFromAppData = (
    serviceTopology: STExtApi['serviceTopology'],
    appData: any
): {componentType: string; manifestUrl: string}[] => {
    const comps = getAppComponents(appData).filter(isNewBuilderComponent)
    return comps.map((comp: any) => {
        const {componentType} = comp.data.componentModel
        const urlKey = `${comp.componentId}_manifestUrl`
        const url = appData.appFields.platform.baseUrls[urlKey]
        const dataFixerVersion = serviceTopology.getServiceTopology().scriptsVersionsMap?.['@wix/santa-data-fixer']
        const manifestGeneratorVersion =
            serviceTopology.getServiceTopology().scriptsVersionsMap?.['blocks-builder-manifest-generator']
        const manifestUrl = url
            .replace('$MANIFEST_MODULE_VERSION$', manifestGeneratorVersion)
            .replace('$DATA_FIXER_VERSION$', dataFixerVersion)
        return {componentType, manifestUrl}
    })
}

/* Cast the Schema type to a Record<string, SchemaFile>
 * On the builder side of things, we want Schemas to remain opaque. Which is why they're defined as unknown.
 * Unfortunately, that requires a type conversion here to be able to use them in the schemaAPI.registerComponentDefinitionAndSchemas method.
 */
function asSchemas(data: Schema): Record<string, SchemaFile> {
    return data as Record<string, SchemaFile>
}

const registerBuilderComponent = (schemaAPI: SchemaAPI, details: BuilderCompDetails): void => {
    const {definition, schemas, identifier} = details
    const allowOverrides = true
    schemaAPI.registerComponentDefinitionAndSchemas(
        identifier,
        {
            componentDefinition: {
                [identifier]: definition
            },
            dataSchemas: asSchemas(schemas.data),
            propertiesSchemas: {},
            otherSchemas: {
                innerElements: asSchemas(schemas.innerElements),
                style: asSchemas(schemas.style),
                multilingualTranslations: asSchemas(schemas.multilingualTranslations),
                presets: asSchemas(schemas.presets),
                slots: asSchemas(schemas.slots)
            }
        },
        allowOverrides
    )
}

async function fetchBuilderCompDetails(
    fetchFn: FetchFn,
    identifier: string,
    manifestUrl: string,
    experimentInstance: Experiment
): Promise<BuilderCompDetails[]> {
    const response = await fetchFn(manifestUrl, {
        headers: {
            'x-wix-extended-timeout': '1'
        }
    })
    const manifest = await response.json()
    return deriveFromManifest(identifier, manifest, experimentInstance)
}

// @ts-expect-error - Unused until builder orchestration is implemented, replaced by editor-platform calling the setManifests method
// eslint-disable-next-line @typescript-eslint/no-unused-vars
async function initialize(dmApis: DmApis): Promise<void> {
    const {rendererModel, builderComponents} = dmApis.extensionAPI as RMApi & BuilderComponentsExtensionAPI & STExtApi
    const csm = rendererModel.getClientSpecMap()
    await Promise.all(
        Object.values(csm ?? {}).map(appData => builderComponents.registerBuilderComponentsForApp(appData))
    )
}

const createExtension = ({environmentContext}: CreateExtensionArgument): Extension => {
    return {
        name: 'builderComponents',
        dependencies: new Set(['rendererModel', 'serviceTopology', 'manifest']),
        createPublicAPI(args: CreateExtArgs): BuilderComponentsPublicExtensionAPI {
            const {builderComponents} = args.extensionAPI as BuilderComponentsExtensionAPI
            return {
                builderComponents: {
                    setManifests: builderComponents.setManifests
                }
            }
        },

        createExtensionAPI(args: CreateExtArgs): BuilderComponentsExtensionAPI {
            const {schemaAPI, serviceTopology} = args.extensionAPI as SchemaExtensionAPI & STExtApi
            const {platform} = args.extensionAPI as PlatformExtensionAPI
            const {pointers, dal} = args
            const {fetchFn} = environmentContext
            const {experimentInstance} = args.coreConfig

            const getComponentDetailsFromCSM = (componentType: string): any => {
                const builderCompData = platform.getBuilderCompAppData(componentType)
                if (!builderCompData) return undefined
                const {appDefinitionId, componentId} = builderCompData
                const appPointer = pointers.rendererModel.getClientSpecMapEntryByAppDefId(appDefinitionId)
                const appData = dal.get(appPointer)
                const {data} = appData.components.find((comp: any) => comp.componentId === componentId)
                return data
            }

            return {
                builderComponents: {
                    setManifests(manifests: Record<string, Builder.ResolvedManifest>): void {
                        if (experimentInstance.isOpen('dm_schemaResourceLoader')) {
                            console.log('Manifests should only be set via schema resource loader')
                            return
                        }
                        for (const [identifier, builderManifest] of Object.entries(manifests)) {
                            const derived = deriveFromManifest(identifier, builderManifest, experimentInstance)
                            for (const compDetails of derived) {
                                registerBuilderComponent(schemaAPI, compDetails)
                            }
                        }
                        console.log('Builder components manifests set')
                    },
                    async registerBuilderComponentsForApp(appData: any): Promise<void> {
                        const comps = extractBuilderCompsFromAppData(serviceTopology, appData)
                        await Promise.all(
                            comps.map(async ({componentType, manifestUrl}) => {
                                const derived = await fetchBuilderCompDetails(
                                    fetchFn,
                                    componentType,
                                    manifestUrl,
                                    experimentInstance
                                )
                                for (const compDetails of derived) {
                                    registerBuilderComponent(schemaAPI, compDetails)
                                }
                            })
                        )
                    },
                    deriveFromManifest,
                    registerBuilderComponent(details: BuilderCompDetails) {
                        registerBuilderComponent(schemaAPI, details)
                    },
                    registerBuilderComponentWithManifest(
                        identifier: string,
                        builderManifest: Builder.ResolvedManifest
                    ) {
                        const derived = deriveFromManifest(identifier, builderManifest, experimentInstance)
                        for (const compDetails of derived) {
                            registerBuilderComponent(schemaAPI, compDetails)
                        }
                    },
                    getBuilderTypes(compPointer: CompRef): BuilderTypes {
                        const compType = getComponentType(dal, compPointer)
                        return builderTypes(schemaAPI, compType)
                    },
                    isBuilder(compType: string) {
                        return !!platform.getBuilderCompAppData(compType)
                    },
                    layout: {
                        getContentFill: (compPointer: CompRef): ContentFill | undefined => {
                            const compType = getComponentType(dal, compPointer)
                            const {editorElement} = getComponentDetailsFromCSM(compType) ?? {}
                            return editorElement.layout?.contentFill
                        }
                    }
                }
            }
        },

        initialize: async (extArgs: InitializeExtArgs) => {
            const {extensionAPI, dal, pointers} = extArgs
            const {hooks} = extensionAPI as HooksExtensionApi
            const {experimentInstance} = extArgs.coreConfig

            //TODO the flag should be removed as well once the experiment is merged since it is a
            // temporary solution to serve the code to editor3 regardless of the state of the experiment
            if (
                experimentInstance.isOpen('dm_builderDataNestingAndDeltaUpdate') ||
                dal.get(pointers.rendererModel.isBuilderComponentModel())
            ) {
                hooks.registerHookWithExtensionArgs(
                    DATA_MODEL_HOOKS.DATA.AFTER_GET_PUBLIC.id,
                    (_extArgs, {componentPointer, dataItem}: AfterPublicGetDataItemEvent) => {
                        return transformBuilderDataToFlat(extArgs, componentPointer, dataItem)
                    }
                )

                hooks.registerHookWithExtensionArgs(
                    DATA_MODEL_HOOKS.DATA.BEFORE_UPDATE_PUBLIC.id,
                    (_extArgs, {componentPointer, dataItem}: BeforePublicUpdateDataItemEvent) => {
                        return transformBuilderDataToNestedObjectsOnDataUpdate(extArgs, componentPointer, dataItem)
                    }
                )

                hooks.registerHookWithExtensionArgs(
                    COMPONENTS_HOOKS.ADD_COMPONENT.BEFORE.id,
                    (_extArgs, {componentDefinition}: ComponentBeforeAddDataEvent) => {
                        return transformBuilderDataToNestedObjectsOnComponentAdd(extArgs, componentDefinition)
                    }
                )

                hooks.registerHookWithExtensionArgs(
                    COMPONENTS_HOOKS.SERIALIZE.DATA_AFTER.id,
                    (_extArgs, {componentPointer, componentDefinition}: SerializeDataAfterEvent) => {
                        if (componentDefinition.data) {
                            componentDefinition.data = transformBuilderDataToFlat(
                                extArgs,
                                componentPointer,
                                componentDefinition.data
                            )
                        }

                        if (componentDefinition.innerElements) {
                            componentDefinition.innerElements = transformInnerElementsBuilderDataToFlat(
                                extArgs,
                                componentDefinition.innerElements
                            )
                        }
                    }
                )
            }
        }
    }
}

export {createExtension, BuilderComponentsApi, BuilderComponentsExtensionAPI}
